// Voice-memo time-logging API — thin wrappers over /api/voice-memos
// (server: voice memo routes; cookie auth + CSRF via lib/api's request helper).
// Staff record a short spoken memo ("CCM 20 minutes, caregiver discussion,
// for John Smith"); the server transcribes + auto-commits asynchronously.

import { api } from './api';

// Upload one recorded memo. audioBase64 has NO data: prefix; durationSec is an
// int 1-300; idempotencyKey (8-64 url-safe chars, e.g. crypto.randomUUID())
// makes retries safe — re-POSTing the same key never duplicates the memo.
// Returns 201 { id, status } (this response = "Saved ✓").
// Throws via api.post on 413 (>12MB decoded), 400 (bad input), or 404
// (practice hasn't enabled voice memos — show the friendly "not enabled" state).
export async function createVoiceMemo({ audioBase64, mimeType, durationSec, idempotencyKey }) {
  return await api.post('/api/voice-memos', {
    audioBase64, mimeType, durationSec, idempotencyKey, source: 'web',
  });
}

// List memos → { memos: [...], reviewCount }. status = csv of statuses or 'all';
// scope = 'mine' | 'practice' (non-admins always get their own regardless);
// reviewCount = needs_review count (own for staff, practice-wide for admin).
export async function listVoiceMemos({ status = 'all', scope = 'mine', limit = 15 } = {}) {
  const qs = `status=${encodeURIComponent(status)}&scope=${encodeURIComponent(scope)}&limit=${encodeURIComponent(limit)}`;
  return await api.get(`/api/voice-memos?${qs}`);
}

// Detail for one memo — adds { transcript, match, error, mimeType } to the
// list shape. match = { status:'one'|'multiple'|'none', patient?, candidates?,
// directoryCandidates? } (directoryCandidates are in the directory but NOT
// enrolled — render greyed, they cannot be committed).
export async function getVoiceMemo(id) {
  return await api.get(`/api/voice-memos/${encodeURIComponent(id)}`);
}

// Same-origin raw audio bytes for <audio controls src=…> (CSP media-src 'self'
// is already configured server-side).
export function voiceMemoAudioUrl(id) {
  return `/api/voice-memos/${encodeURIComponent(id)}/audio`;
}

// Human commit after review. Payload depends on the memo's intent —
// timelog: { patientId, minutes, program, activityType, notes };
// note:    { patientId, noteKind, body };
// order:   { patientId, delivery, sessionId } — sessionId is the staged session
//          the CARD displayed (detail.staged.sessionId). The server refuses with
//          `order_stale` unless the memo still points at it, so a card whose
//          bundle was refreshed underneath it can never file the replacement.
// → { id, status:'committed', intent, entryId|noteId, patientId, ... }.
// Throws on 422 { error, code } (surface error text inline) and 409 (memo
// not in needs_review/failed, or a concurrent commit/discard won the claim).
export async function commitVoiceMemo(id, payload) {
  return await api.post(`/api/voice-memos/${encodeURIComponent(id)}/commit`, payload || {});
}

// Discard a memo → { id, status:'discarded' }.
export async function discardVoiceMemo(id) {
  return await api.post(`/api/voice-memos/${encodeURIComponent(id)}/discard`, {});
}

// Human intent correction → { id, status:'transcribed', intent }. The worker
// re-extracts under the corrected intent on its next tick. 409 when the memo
// moved on or a note attempt already reached Elation.
export async function rerouteVoiceMemo(id, intent) {
  return await api.post(`/api/voice-memos/${encodeURIComponent(id)}/reroute`, { intent });
}

// Resolve an indeterminate note attempt. action: 'link' (+noteId), 'retry'
// (admin; optional patientId/noteKind/body override), or 'handled'.
export async function resolveVoiceMemoNote(id, payload) {
  return await api.post(`/api/voice-memos/${encodeURIComponent(id)}/resolve-note`, payload);
}

// Hand an order/appointment memo to Order Chat → { id, status:'transferred',
// sessionId, deepLink }. The staged session becomes the caller's live chat
// thread; the memo auto-commits when the chat completes (worker sweep).
export async function transferVoiceMemoToOrderChat(id) {
  return await api.post(`/api/voice-memos/${encodeURIComponent(id)}/to-order-chat`, {});
}

// Undo a memo-BOOKED appointment (status-flip cancel; server pre-flights
// future + still-active, CAS against double-undo) → { id, undone: true }.
export async function undoVoiceMemoAppointment(id) {
  return await api.post(`/api/voice-memos/${encodeURIComponent(id)}/undo-appointment`, {});
}
